import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { concatLatestFrom } from '@ngrx/operators';
import { Observable } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { AuthService } from '@telespot/web-core';
import { WorkspaceItem } from '@telespot/domain';
import { RoleData, RolesStore } from '@telespot/authentication/ui';
import { AffiliatedUserData, AffiliatedUsersStore } from '../../stores/user/affiliated-user.store';
import { UserMembershipsEffects } from '../../stores/user/user-memberships.effects';
import { WorkspaceMemberData } from '../../stores/user/workspace-members.state';
import { WorkspacesEffects } from '../../stores/workspaces.effects';

/**
 * Base facade for user management components.
 * Extends ComponentStore with empty state as per facade conventions.
 * Coordinates data from AffiliatedUsersStore, RolesStore, and WorkspacesEffects.
 */
@Injectable()
export abstract class BaseUserFacade extends ComponentStore<Record<string, never>> {
  public readonly hasChanges$: Observable<boolean>;
  public readonly error$: Observable<string | undefined>;

  constructor(
    protected readonly affiliatedUserStore: AffiliatedUsersStore,
    protected readonly userMembershipsEffects: UserMembershipsEffects,
    protected readonly rolesStore: RolesStore,
    protected readonly workspacesEffects: WorkspacesEffects,
    protected readonly authService: AuthService
  ) {
    super({});
    this.hasChanges$ = this.select(
      this.affiliatedUserStore.hasChanges$,
      this.userMembershipsEffects.membershipsToAdd$,
      this.userMembershipsEffects.membershipsToRemove$,
      (rolesChanged, membershipsToAdd, membershipsToRemove) =>
        rolesChanged || membershipsToAdd.length > 0 || membershipsToRemove.length > 0
    );
    this.error$ = this.select(
      this.affiliatedUserStore.error$,
      this.userMembershipsEffects.loadError$,
      (affiliatedError, membershipError) => membershipError || affiliatedError
    );
    this.onUpdateUserTriggered(this.affiliatedUserStore.updateUserTriggered$);
  }

  // Delegated selectors from RolesStore
  public get roles$() {
    return this.rolesStore.roles$;
  }

  public get rolesLoading$() {
    return this.rolesStore.loading$;
  }

  // Delegated selectors from AffiliatedUsersStore
  public get updatedRoles$() {
    return this.affiliatedUserStore.updatedRoles$;
  }

  public get memberOperationError$() {
    return this.userMembershipsEffects.membershipOperationError$;
  }

  public get selectedWorkspaces$() {
    return this.userMembershipsEffects.selectedWorkspaces$;
  }

  public get selectedWorkspacesCount$() {
    return this.userMembershipsEffects.selectedWorkspacesCount$;
  }

  // Delegated selectors from WorkspacesEffects
  public get workspaces$() {
    return this.workspacesEffects.workspaces$;
  }

  public get hasMoreWorkspaces$() {
    return this.workspacesEffects.hasMoreItems$;
  }

  public get totalWorkspaces$() {
    return this.workspacesEffects.totalWorkspaces$;
  }

  // Derived selector from AuthService
  public get isAdmin$() {
    return this.authService.currentOrganization$.pipe(map(() => this.authService.hasRoles(['admin'])));
  }

  // Delegated updaters from stores
  public readonly grantRole = (role: RoleData) => {
    this.affiliatedUserStore.markRoleAsGranted(role);
  };

  public readonly revokeRole = (role: RoleData) => {
    this.affiliatedUserStore.markRoleAsRevoked(role);
  };

  public readonly addToWorkspaces = (workspaces: WorkspaceItem[]) => {
    this.userMembershipsEffects.addToWorkspaces(workspaces);
  };

  public readonly removeFromWorkspaces = (workspaces: WorkspaceItem[]) => {
    this.userMembershipsEffects.removeFromWorkspaces(workspaces);
  };

  public readonly searchWorkspace = (term: string) => {
    this.workspacesEffects.setSearchTerm(term);
  };

  // Delegated effects
  public readonly loadMoreWorkspaces = this.effect((trigger$: Observable<boolean>) =>
    trigger$.pipe(
      map((hasMoreItems) => {
        if (hasMoreItems) {
          this.workspacesEffects.loadMoreWorkspaces(undefined);
        }
      })
    )
  );

  // Load every workspace matching the current filter so "select all" applies to
  // the whole result set, not just the pages already scrolled into view.
  public readonly loadAllWorkspaces = () => {
    this.workspacesEffects.loadAllWorkspaces();
  };

  /**
   * Initialize shared resources: fetch roles and set organization ID on workspaces store.
   * Calls the abstract initializeSpecific hook for subclass-specific initialization.
   */
  public readonly initialize = this.effect((input$: Observable<{ organizationId: string; maxUsers: number }>) =>
    input$.pipe(
      map(({ organizationId, maxUsers }) => {
        // Shared initialization
        this.rolesStore.fetchRoles();
        this.workspacesEffects.setOrganizationId(organizationId);
        this.userMembershipsEffects.setOrganizationId(organizationId);

        // Subclass-specific initialization
        this.initializeSpecific({ organizationId, maxUsers });
      })
    )
  );

  private readonly onUpdateUserTriggered = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      concatLatestFrom(() => [this.affiliatedUserStore.selectedUser$, this.userMembershipsEffects.activeUser$]),
      filter(
        (payload): payload is [void, AffiliatedUserData, WorkspaceMemberData] =>
          payload[1] !== undefined || payload[2] !== undefined
      ),
      tap(([, selectedUser, activeUser]: [void, AffiliatedUserData, WorkspaceMemberData]) => {
        if (activeUser === undefined) {
          this.userMembershipsEffects.setActiveUser({
            clearMemberships: false,
            activeUser: {
              id: selectedUser.id,
              username: selectedUser.username,
              avatarUrl: selectedUser.avatarUrl,
              emailVerified: selectedUser.emailVerified,
              membershipId: undefined,
            },
          });
        }
        this.userMembershipsEffects.syncWorkspaceChanges();
      })
    )
  );

  /**
   * Abstract hook for subclass-specific initialization.
   * Called by the initialize effect after shared initialization completes.
   */
  protected abstract initializeSpecific(params: { organizationId: string; maxUsers: number }): void;
}
